import { initializeApollo } from '@jusbrasil-web/shared-apollo-client'
import { ReactNode } from 'react'

import { ENTITY_CONFIG_MAP, EntityEnum, EntityType } from './config'

export type Entity = {
  id: string
  type: EntityType
  loaded: boolean
  data: unknown
  url: string
}

type GetElementsFilteredByAttributesProps = {
  elements: NodeListOf<HTMLElement> | never[]
  attributes: string[]
}

const TRIGGER_TYPE_MAP = Object.entries(ENTITY_CONFIG_MAP).reduce((acc, [type, { selectors }]) => {
  for (const selector of selectors) {
    acc[selector] = type as EntityType
  }
  return acc
}, {} as Record<string, EntityType>)

export const ALL_SELECTORS = Object.keys(TRIGGER_TYPE_MAP)

const CACHE = new Map<string, Entity>()

const client = initializeApollo()

export async function onFetchEntityData(type: EntityType, id: string): Promise<unknown | null> {
  const { Component, mapIdToVariables } = ENTITY_CONFIG_MAP[type]
  const query = Component.query
  const variables = mapIdToVariables(id)
  if (variables === null) {
    return Promise.reject('variables not found')
  }

  const { data } = await client.query({ query, variables })
  return data ?? null
}

export function updateEntityFromElement(
  element: HTMLElement,
  opts: {
    setEntity(entity: Entity | null): void
    onError(err: Error): void
    cache?: Map<string, Entity>
    popoverEntityType?: EntityType
  }
) {
  const { setEntity, onError, cache = CACHE, popoverEntityType } = opts

  const url = element.getAttribute('href') || ''
  const id = element.getAttribute('rel') || element.getAttribute('data-profile-tooltip')
  if (!id) return

  const type = mapEntityFromElement(element, popoverEntityType)
  if (!type) return

  const cached = cache.get(id)

  if (cached?.loaded) {
    setEntity(cached)
  } else if (!cached) {
    const entity = { id, type, data: null, loaded: false, url }

    cache.set(id, entity)

    setEntity(entity)
    onFetchEntityData(type, id)
      .then((data) => {
        if (data) {
          const newEntity = { ...entity, data, loaded: true }
          cache.set(id, newEntity)
          setEntity(newEntity)
        } else throw new Error('Entity not found')
      })
      .catch((err) => {
        cache.delete(id)
        setEntity(null)
        onError(err)
      })
  }
}

export function renderContentFromEntity(
  entity: Entity,
  context = '',
  hasLRSubscription = false
): ReactNode {
  const { Component } = ENTITY_CONFIG_MAP[entity.type]
  return (
    <Component
      query={entity.data}
      entityType={entity.type}
      entityUrl={entity.url}
      context={context}
      hasLRSubscription={hasLRSubscription}
    />
  )
}

export function mapEntityFromElement(element: HTMLElement, popoverEntityType?: EntityType) {
  const selector = ALL_SELECTORS.find((k) => element.matches(k))
  if (!selector) return

  if (popoverEntityType === EntityEnum.TRACKER && selector === '.entity-cite') {
    return EntityEnum.TRACKER
  }

  return TRIGGER_TYPE_MAP[selector]
}

export function getElementsFilteredByAttributes({
  elements,
  attributes,
}: GetElementsFilteredByAttributesProps) {
  return Array.from(elements).filter((element) =>
    attributes.some((attr) => element.getAttribute(attr))
  )
}
